import React, { useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useApp } from "@/context/AppContext";
import { useSave } from "@/hooks/useSave";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Loader2, Wallet, Lightbulb, Bookmark, ArrowLeft } from "lucide-react";

const CATEGORIES = ["Travel", "Self-Care", "Lifestyle", "Fashion"];
const BUDGETS = ["Smart", "Premium", "Luxury", "Ultra-Luxe"];
const DURATIONS = ["1 day", "3 days", "5 days", "1 week"];

const Chip = ({ active, children, onClick, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`px-4 py-2 rounded-full text-sm transition-colors duration-200 border ${
      active
        ? "bg-gradient-to-r from-[#F3E5AB] to-[#D4AF37] text-[#1a1a12] border-transparent font-medium"
        : "border-amber-400/25 text-slate-300 hover:border-amber-400/50"
    }`}
  >
    {children}
  </button>
);

export const PlannerModal = () => {
  const { plannerOpen, setPlannerOpen } = useApp();
  const { save } = useSave();
  const [form, setForm] = useState({ category: "Travel", destination: "", budget: "Luxury", duration: "3 days", guests: "2", notes: "" });
  const [loading, setLoading] = useState(false);
  const [plan, setPlan] = useState(null);

  const up = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const generate = async () => {
    setLoading(true);
    setPlan(null);
    try {
      const res = await api.post("/planner/generate", form);
      setPlan(res.data);
    } catch (e) {
      toast.error("The concierge is unavailable", { description: "Please try again in a moment." });
    } finally {
      setLoading(false);
    }
  };

  const savePlan = () => {
    save({
      item_type: "plan",
      title: plan.title,
      subtitle: `${form.category} · ${form.budget}`,
      image: "",
      meta: { plan, form },
    });
  };

  const reset = () => setPlan(null);

  return (
    <Dialog open={plannerOpen} onOpenChange={(o) => { setPlannerOpen(o); if (!o) { setPlan(null); } }}>
      <DialogContent className="sm:max-w-2xl max-h-[88vh] overflow-y-auto bg-[#0b0b10] border border-[rgba(212,175,55,0.2)] text-white" data-testid="planner-modal">
        <DialogTitle className="sr-only">Plan My Lifestyle — AI Concierge</DialogTitle>
        <DialogDescription className="sr-only">Generate a personalized luxury lifestyle plan.</DialogDescription>
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-full border border-amber-400/40 flex items-center justify-center">
            <Sparkles className="text-champagne" size={18} />
          </div>
          <div>
            <p className="text-[10px] tracking-[0.25em] text-amber-400/80 uppercase">AI Concierge</p>
            <h3 className="font-serif text-2xl text-champagne">Plan My Lifestyle</h3>
          </div>
        </div>

        {!plan && (
          <div className="space-y-6 mt-4" data-testid="planner-form">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-slate-400">Focus</label>
              <div className="flex flex-wrap gap-2 mt-3">
                {CATEGORIES.map((c) => (
                  <Chip key={c} active={form.category === c} onClick={() => up("category", c)} testid={`planner-cat-${c.toLowerCase()}`}>{c}</Chip>
                ))}
              </div>
            </div>

            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-slate-400">Destination / theme</label>
              <Input
                value={form.destination}
                onChange={(e) => up("destination", e.target.value)}
                placeholder="e.g. Santorini escape, calm reset week, capsule wardrobe"
                data-testid="planner-destination-input"
                className="mt-3 bg-[#14141c] border-amber-400/15 text-white placeholder:text-slate-600"
              />
            </div>

            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-slate-400">Budget</label>
              <div className="flex flex-wrap gap-2 mt-3">
                {BUDGETS.map((b) => (
                  <Chip key={b} active={form.budget === b} onClick={() => up("budget", b)} testid={`planner-budget-${b.toLowerCase()}`}>{b}</Chip>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-slate-400">Duration</label>
                <div className="flex flex-wrap gap-2 mt-3">
                  {DURATIONS.map((d) => (
                    <Chip key={d} active={form.duration === d} onClick={() => up("duration", d)} testid={`planner-dur-${d.replace(/\s/g, "")}`}>{d}</Chip>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-slate-400">Guests</label>
                <Input
                  value={form.guests}
                  onChange={(e) => up("guests", e.target.value)}
                  data-testid="planner-guests-input"
                  className="mt-3 bg-[#14141c] border-amber-400/15 text-white"
                />
              </div>
            </div>

            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-slate-400">Notes</label>
              <Textarea
                value={form.notes}
                onChange={(e) => up("notes", e.target.value)}
                placeholder="Anything we should know? dietary, pace, vibe…"
                data-testid="planner-notes-input"
                className="mt-3 bg-[#14141c] border-amber-400/15 text-white placeholder:text-slate-600 resize-none"
              />
            </div>

            <Button
              onClick={generate}
              disabled={loading}
              data-testid="planner-generate-btn"
              className="w-full rounded-full bg-gradient-to-r from-[#F3E5AB] to-[#D4AF37] text-[#1a1a12] font-medium h-12 hover:opacity-90"
            >
              {loading ? (<><Loader2 className="animate-spin mr-2" size={18} /> Crafting your plan…</>) : (<><Sparkles size={18} className="mr-2" /> Generate My Plan</>)}
            </Button>
          </div>
        )}

        {plan && (
          <div className="space-y-6 mt-4 animate-fade-up" data-testid="planner-result">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h4 className="font-serif text-2xl text-champagne">{plan.title}</h4>
                <p className="text-slate-400 text-sm mt-2 leading-relaxed">{plan.summary}</p>
              </div>
            </div>

            <div className="space-y-4">
              {(plan.days || []).map((d) => (
                <div key={d.day} className="rounded-xl bg-[#13131a] border border-amber-400/10 p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="w-7 h-7 rounded-full bg-amber-400/15 text-champagne text-sm flex items-center justify-center font-medium">{d.day}</span>
                    <span className="font-serif text-lg text-white">{d.title}</span>
                  </div>
                  <ul className="space-y-3">
                    {(d.activities || []).map((a, i) => (
                      <li key={i} className="flex gap-4">
                        <span className="text-xs text-amber-400/80 w-20 shrink-0 pt-0.5">{a.time}</span>
                        <div>
                          <p className="text-sm text-white">{a.name}</p>
                          <p className="text-xs text-slate-400 mt-0.5">{a.detail}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            {plan.budget && (
              <div className="rounded-xl bg-[#13131a] border border-amber-400/10 p-5">
                <div className="flex items-center gap-2 mb-3 text-champagne"><Wallet size={16} /><span className="font-medium">Budget · {plan.budget.total}</span></div>
                <div className="flex flex-wrap gap-x-6 gap-y-2">
                  {(plan.budget.breakdown || []).map((b, i) => (
                    <span key={i} className="text-sm text-slate-400">{b.label}: <span className="text-slate-200">{b.amount}</span></span>
                  ))}
                </div>
              </div>
            )}

            {plan.tips?.length > 0 && (
              <div className="rounded-xl bg-[#13131a] border border-amber-400/10 p-5">
                <div className="flex items-center gap-2 mb-3 text-champagne"><Lightbulb size={16} /><span className="font-medium">Concierge tips</span></div>
                <ul className="space-y-2 list-disc list-inside text-sm text-slate-400">
                  {plan.tips.map((t, i) => <li key={i}>{t}</li>)}
                </ul>
              </div>
            )}

            <div className="flex gap-3">
              <Button variant="outline" onClick={reset} data-testid="planner-back-btn" className="rounded-full border-amber-400/30 text-slate-200 hover:bg-amber-400/10 bg-transparent">
                <ArrowLeft size={16} className="mr-2" /> New plan
              </Button>
              <Button onClick={savePlan} data-testid="planner-save-btn" className="flex-1 rounded-full bg-gradient-to-r from-[#F3E5AB] to-[#D4AF37] text-[#1a1a12] font-medium hover:opacity-90">
                <Bookmark size={16} className="mr-2" /> Save to Dashboard
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
